import { ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { usePermissions } from '@/contexts/PermissionsContext'
import { LoadingState } from '@/components/primitives'
import { AccessDenied } from './AccessDenied'
import {
  getDefaultLandingPath,
  getNavRequirement,
  isNavItemAuthorized,
  type NavItemId,
  type NavRequirement,
} from '@/lib/navigation'

interface AuthorizedRouteProps {
  children: ReactNode
  /** Look up requirements from the navigation registry by id. */
  navId?: NavItemId
  /** Or pass explicit requirements (bypasses the registry). */
  requirement?: Omit<NavRequirement, 'id' | 'path' | 'labelKey'>
}

/**
 * Wraps a route element and checks plan/permissions/role (from
 * PermissionsContext + AuthContext) against a navigation.ts entry. Renders a
 * localized AccessDenied state on failure instead of the protected page.
 */
export function AuthorizedRoute({ children, navId, requirement }: AuthorizedRouteProps) {
  const { plan } = useAuth()
  const { role, can, isLoading } = usePermissions()

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center">
        <LoadingState type="page" />
      </div>
    )
  }

  const resolved: Pick<NavRequirement, 'requiredPlan' | 'requiredPermission' | 'roles'> | undefined =
    requirement ?? (navId ? getNavRequirement(navId) : undefined)

  if (!resolved) {
    return <>{children}</>
  }

  const authorized = isNavItemAuthorized(
    { id: navId ?? 'dashboard', path: '', labelKey: '', ...resolved },
    { plan, role, can },
  )

  if (!authorized) {
    return <AccessDenied landingPath={getDefaultLandingPath()} />
  }

  return <>{children}</>
}
